import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { ArrowUpRight, Clock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import insightsData from "@/data/insightsData";

const insights = insightsData.slice(0, 3).map((d) => ({
  title: d.title,
  excerpt: d.excerpt,
  tag: d.category,
  readTime: d.readTime,
  image: d.image,
  slug: d.slug,
}));

/* Flex ratios: idle cards are narrower, default-active card is wider */
const DEFAULT_ACTIVE = 0;
const IDLE_FLEX = 1;
const ACTIVE_FLEX = 2.6;

export default function Insights() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  const activeIndex = hovered !== null ? hovered : DEFAULT_ACTIVE;

  return (
    <section
      data-testid="insights-section"
      className="py-[30px] md:py-[50px] bg-slate-50/50"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-end justify-between mb-[31px] md:mb-[51px]"
        >
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 font-body mb-4 block">
              Perspectives
            </span>
            <h2
              data-testid="insights-heading"
              className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a]"
            >
              Our latest insights
            </h2>
          </div>
          <Link
            to="/insights"
            data-testid="insights-view-all"
            className="hidden md:inline-flex items-center gap-2 px-[18px] py-[10px] text-[14px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-full hover:border-blue-200 hover:text-blue-600 transition-all duration-200 hover:-translate-y-0.5"
          >
            View all
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>

        {/* ---- Desktop: horizontal accordion ---- */}
        <div
          className="hidden md:flex gap-4 h-[480px]"
          onMouseLeave={() => setHovered(null)}
        >
          {insights.map((insight, i) => {
            const isActive = activeIndex === i;

            return (
              <motion.article
                key={insight.title}
                data-testid={`insight-card-${i + 1}`}
                onMouseEnter={() => setHovered(i)}
                onClick={() => navigate(`/insights/${insight.slug}`)}
                initial={{ opacity: 0, y: 50, scale: 0.96 }}
                animate={
                  isInView
                    ? {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        flex: isActive ? ACTIVE_FLEX : IDLE_FLEX,
                      }
                    : {}
                }
                transition={{
                  flex: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                  opacity: { duration: 0.6, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
                  y: { duration: 0.6, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
                  scale: { duration: 0.6, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
                }}
                className="relative rounded-[1.5rem] overflow-hidden cursor-pointer min-w-0"
                style={{ flex: isActive ? ACTIVE_FLEX : IDLE_FLEX }}
              >
                {/* Image — always visible, covers the card */}
                <img
                  src={insight.image}
                  alt={insight.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out"
                  style={{ transform: isActive ? "scale(1.05)" : "scale(1)" }}
                  loading="lazy"
                />

                {/* Dark gradient overlay — strong text-safe zone in bottom 40% */}
                <div
                  className="absolute inset-0 transition-all duration-300 ease-in-out"
                  style={{
                    background: isActive
                      ? "linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 30%, rgba(0,0,0,0.50) 55%, rgba(0,0,0,0.90) 100%)"
                      : "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.18) 40%, rgba(0,0,0,0.70) 100%)",
                  }}
                />

                {/* ---- Idle state: title only ---- */}
                <div
                  className="absolute inset-0 flex items-end p-6 transition-opacity duration-300"
                  style={{ opacity: isActive ? 0 : 1, pointerEvents: isActive ? "none" : "auto", textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}
                >
                  <h3 className="font-body text-lg font-bold text-white tracking-tight leading-snug line-clamp-2">
                    {insight.title}
                  </h3>
                </div>

                {/* ---- Active state: text-safe zone with local blur ---- */}
                <div
                  className="absolute inset-x-0 bottom-0 h-[55%] transition-opacity duration-300 backdrop-blur-[10px]"
                  style={{
                    opacity: isActive ? 1 : 0,
                    pointerEvents: isActive ? "auto" : "none",
                    maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%)',
                  }}
                />
                <div
                  className="absolute inset-x-0 bottom-0 flex flex-col justify-end p-8 transition-opacity duration-300"
                  style={{
                    opacity: isActive ? 1 : 0,
                    pointerEvents: isActive ? "auto" : "none",
                    textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                  }}
                >
                  {/* Tag + read time */}
                  <motion.div
                    animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                    transition={{ duration: 0.35, delay: isActive ? 0.08 : 0 }}
                    className="flex items-center gap-3 mb-4"
                  >
                    <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white bg-white/20 backdrop-blur-sm rounded-full border border-white/15">
                      {insight.tag}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-white/80">
                      <Clock className="w-3 h-3" />
                      {insight.readTime}
                    </span>
                  </motion.div>

                  {/* Title */}
                  <motion.h3
                    animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                    transition={{ duration: 0.35, delay: isActive ? 0.12 : 0 }}
                    className="font-body text-2xl font-bold text-white tracking-tight leading-snug mb-3"
                  >
                    {insight.title}
                  </motion.h3>

                  {/* Excerpt */}
                  <motion.p
                    animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                    transition={{ duration: 0.35, delay: isActive ? 0.18 : 0 }}
                    className="font-body text-sm text-white/90 leading-relaxed mb-5 max-w-md line-clamp-3"
                  >
                    {insight.excerpt}
                  </motion.p>

                  {/* Read more link */}
                  <motion.div
                    animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                    transition={{ duration: 0.3, delay: isActive ? 0.24 : 0 }}
                  >
                    <Link
                      to={`/insights/${insight.slug}`}
                      data-testid={`insight-read-more-${i + 1}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-white group/link"
                    >
                      Read more
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </Link>
                  </motion.div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* ---- Mobile: stacked cards ---- */}
        <div className="md:hidden flex flex-col gap-5">
          {insights.map((insight, i) => (
            <motion.article
              key={insight.title}
              data-testid={`insight-card-mobile-${i + 1}`}
              onClick={() => navigate(`/insights/${insight.slug}`)}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.12 * i }}
              className="relative rounded-2xl overflow-hidden h-[300px] cursor-pointer"
            >
              <img
                src={insight.image}
                alt={insight.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/90 bg-white/15 backdrop-blur-sm rounded-full border border-white/10 mb-3">
                  {insight.tag}
                </span>
                <h3 className="font-body text-lg font-semibold text-white tracking-tight leading-snug mb-2">
                  {insight.title}
                </h3>
                <p className="font-body text-sm text-white/60 leading-relaxed line-clamp-2">
                  {insight.excerpt}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Mobile view all */}
        <div className="md:hidden mt-8 text-center">
          <Link
            to="/insights"
            data-testid="insights-view-all-mobile"
            className="inline-flex items-center gap-2 px-[18px] py-[10px] text-[14px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-full hover:-translate-y-0.5 transition-all duration-200"
          >
            View all
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
